import { formatNameLastFirst, getProductName } from '@strata/helper-functions';
import { WarningDialog } from '@strata/react-dialog';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { useNavigate } from 'react-router';

import { AppRoutes } from 'services/routes';
import { type IntakeCase, usePostReopenIntakeCaseMutation } from 'store/api/intakeCaseApi';
import { useGetProductsMapQuery } from 'store/api/referenceApi';

interface ReopenIntakeCaseDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<
        IntakeCase,
        | 'intakeCaseId'
        | 'memberId'
        | 'memberFirstName'
        | 'memberLastName'
        | 'productServiceOid'
        | 'isBrand'
    >;
}

export const ReopenIntakeCaseDialog = ({
    isOpen,
    onClose,
    intakeCase,
}: ReopenIntakeCaseDialogProps) => {
    const navigate = useNavigate();
    const [postReopenIntakeCase] = usePostReopenIntakeCaseMutation();
    const { data: productsMap } = useGetProductsMapQuery();
    return (
        <WarningDialog
            dialogTitle="Confirm Reopen Intake Case"
            width={600}
            isOpen={isOpen}
            primaryButtonLabel="Yes, Reopen Intake Case"
            onPrimaryButtonClick={() => {
                void (async () => {
                    const newRequestId = await postReopenIntakeCase(
                        intakeCase.intakeCaseId,
                    ).unwrap();
                    SuccessToast({ message: 'Intake Case has been reopened successfully.' });
                    onClose();
                    navigate(AppRoutes.intakeCaseDetails(newRequestId));
                })();
            }}
            secondaryButtonLabel="No, Return To Intake Case Details"
            onSecondaryButtonClick={onClose}
            dialogContent={
                <>
                    <div className="margin__bottom--medium">
                        Are you sure you want to reopen this intake case? A new intake case will be
                        created with the same details.
                    </div>
                    <div className="flex__gap--large">
                        <DetailLabelValuePair
                            label="Patient"
                            value={formatNameLastFirst(
                                intakeCase.memberLastName,
                                intakeCase.memberFirstName,
                            )}
                        />
                        <DetailLabelValuePair label="Member ID" value={intakeCase.memberId} />
                        <DetailLabelValuePair
                            label="Product"
                            value={getProductName(
                                productsMap,
                                intakeCase.productServiceOid,
                                intakeCase.isBrand,
                            )}
                        />
                    </div>
                </>
            }
        />
    );
};
